/** Typed read/write of URL query parameters, so every lab state is a shareable link. */

export type ParamValue = number | boolean | string;

/** Parse one raw query value into the type of `fallback`. Invalid input yields the fallback. */
export function parseParam<T extends ParamValue>(raw: string | null, fallback: T): T {
  if (raw === null) return fallback;
  if (typeof fallback === "number") {
    if (raw.trim() === "") return fallback;
    const n = Number(raw);
    return (Number.isFinite(n) ? n : fallback) as T;
  }
  if (typeof fallback === "boolean") {
    if (raw === "1" || raw === "true" || raw === "") return true as T;
    if (raw === "0" || raw === "false") return false as T;
    return fallback;
  }
  return raw as T;
}

/** Serialise a value for the query string (booleans as 1/0, numbers to 6 significant digits). */
export function formatParam(value: ParamValue): string {
  if (typeof value === "boolean") return value ? "1" : "0";
  if (typeof value === "number") return String(Number(value.toPrecision(6)));
  return value;
}

/** Read every key of `defaults` from a query string, typed by the default values. */
export function readParams<T extends Record<string, ParamValue>>(
  defaults: T,
  search: string | URLSearchParams,
): T {
  const q = typeof search === "string" ? new URLSearchParams(search) : search;
  const out = { ...defaults };
  for (const key of Object.keys(defaults) as (keyof T & string)[]) {
    out[key] = parseParam(q.get(key), defaults[key]);
  }
  return out;
}

/**
 * Write values into a query string. Values equal to their default are removed to keep
 * links short; keys not in `values` are left untouched (e.g. `t`, `seed`).
 */
export function writeParams(
  values: Record<string, ParamValue>,
  search: string | URLSearchParams = "",
  defaults: Record<string, ParamValue> = {},
): URLSearchParams {
  const q = new URLSearchParams(typeof search === "string" ? search : search.toString());
  for (const [key, value] of Object.entries(values)) {
    if (key in defaults && defaults[key] === value) q.delete(key);
    else q.set(key, formatParam(value));
  }
  return q;
}

/** Read a single param from the current page URL (browser only; fallback elsewhere). */
export function urlParam<T extends ParamValue>(key: string, fallback: T): T {
  if (typeof location === "undefined") return fallback;
  return parseParam(new URLSearchParams(location.search).get(key), fallback);
}

/** Replace the page URL's query with `q` without adding a history entry (browser only). */
export function replaceUrlQuery(q: URLSearchParams): void {
  if (typeof history === "undefined") return;
  const s = q.toString();
  history.replaceState(null, "", `${location.pathname}${s ? `?${s}` : ""}${location.hash}`);
}

/** The shape of a lab param description that `sanitizeValues` needs. */
export type ParamSpec =
  | { type: "number"; value: number; min: number; max: number }
  | { type: "boolean"; value: boolean }
  | { type: "select"; value: string; options: readonly string[] }
  | { type: "color"; value: string };

/**
 * Bring values read from a URL back inside their descriptions: numbers clamped to their
 * range, selects to one of their options, colours to a hex colour. Hand-edited or stale
 * links then still build a valid scene.
 */
export function sanitizeValues(
  params: Record<string, ParamSpec>,
  values: Record<string, ParamValue>,
): void {
  for (const [key, def] of Object.entries(params)) {
    const v = values[key];
    if (def.type === "number") {
      const n = typeof v === "number" && Number.isFinite(v) ? v : def.value;
      values[key] = Math.min(def.max, Math.max(def.min, n));
    } else if (def.type === "select") {
      if (!def.options.includes(v as string)) values[key] = def.value;
    } else if (def.type === "color") {
      if (typeof v !== "string" || !/^#[0-9a-f]{6}$/i.test(v)) values[key] = def.value;
    } else if (typeof v !== "boolean") {
      values[key] = def.value;
    }
  }
}
